/**
 * Fractional-index position math for the dataspace entity tree.
 *
 * Positions are lexicographic string keys sorted with localeCompare. This module
 * is intentionally dependency-free (no logger / config / window) so it can be unit
 * tested directly and reused without pulling in the store's module graph.
 */

/**
 * Generate a key that sorts strictly between `before` and `after` (either may be
 * null for "start"/"end"). When the two neighbours are equal no such key exists —
 * callers must detect that and rebalance via generateSpacedPositions instead.
 */
export function generatePositionBetween(before: string | null, after: string | null): string {
  // Collision: neighbours are equal — no key can sort strictly between them.
  // Return a deterministic key after the collision point; callers should rebalance.
  if (before && after && before === after) {
    console.warn('[FractionalIndex] ⚠️ Position collision detected — caller should rebalance', { before, after });
    return before + '0';
  }

  if (!before && !after) return 'M';
  if (!after) return before + 'M';
  if (!before) {
    const firstChar = after[0];
    const firstVal = charToBase36(firstChar);
    if (firstVal > 0) {
      const mid = Math.floor(firstVal / 2);
      return base36ToChar(mid);
    }
    return '!' + after;
  }

  let pos = 0;
  while (pos < before.length && pos < after.length && before[pos] === after[pos]) {
    pos++;
  }

  const prefix = before.slice(0, pos);

  if (pos === before.length) {
    const afterSuffix = after.slice(before.length);
    const firstAfterChar = afterSuffix[0];
    const firstAfterVal = charToBase36(firstAfterChar);

    if (firstAfterVal >= 2) {
      const midVal = Math.floor(firstAfterVal / 2);
      return before + base36ToChar(midVal);
    }
    return before + '0' + 'M';
  }

  if (pos === after.length) {
    return after + 'V';
  }

  const beforeChar = before[pos];
  const afterChar = after[pos];
  const beforeVal = charToBase36(beforeChar);
  const afterVal = charToBase36(afterChar);

  if (afterVal - beforeVal >= 2) {
    const midVal = Math.floor((beforeVal + afterVal) / 2);
    return prefix + base36ToChar(midVal);
  }

  return before + 'M';
}

/**
 * Generate `count` clean, evenly-spaced position keys for rebalancing a parent's
 * children. Keys are fixed-width uppercase strings (A–Z), which sort identically
 * under localeCompare and ASCII, are strictly increasing, and leave gaps so that
 * subsequent single-item fractional inserts (generatePositionBetween) have room.
 *
 * Used when a fractional insert is degenerate — i.e. neighbouring siblings collide
 * (e.g. they all share the backend default 'M'), so no key can sort strictly
 * between them. Rebalancing assigns distinct keys to the whole sibling list so the
 * dropped item lands, and stays, where the user placed it.
 */
export function generateSpacedPositions(count: number): string[] {
  if (count <= 0) return [];
  const ALPHA = 26;
  let width = 1;
  while (Math.pow(ALPHA, width) < (count + 1) * 2) width++;
  const slots = Math.pow(ALPHA, width);
  const step = Math.max(1, Math.floor(slots / (count + 1)));
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    let v = Math.min(slots - 1, (i + 1) * step);
    let key = '';
    for (let w = 0; w < width; w++) {
      key = String.fromCharCode(65 + (v % ALPHA)) + key;
      v = Math.floor(v / ALPHA);
    }
    out.push(key);
  }
  return out;
}

export function charToBase36(char: string): number {
  const code = char.charCodeAt(0);
  if (code === 33) return -1;
  if (code >= 48 && code <= 57) return code - 48;
  if (code >= 65 && code <= 90) return code - 65 + 10;
  if (code >= 97 && code <= 122) return code - 97 + 36;
  return 0;
}

export function base36ToChar(val: number): string {
  if (val < 10) return String.fromCharCode(val + 48);
  if (val < 36) return String.fromCharCode(val - 10 + 65);
  if (val < 62) return String.fromCharCode(val - 36 + 97);
  return '0';
}
